import { faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import Skeleton from "react-loading-skeleton";
import _ from "lodash";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { errorToastr, warningToastr, toLocalDateTime, infoToastr, RemoveSecFromTime } from "../../../../HelperMethods";
import { filterBy } from "@progress/kendo-data-query";
import Collapsible from "react-collapsible";
import { getReqShiftService, getShiftTypesService } from "./Services";
import { ErrorComponent } from "../../../ReusableComponents";
import {calculateDuration, parseTimeString, generateRecurrenceRule} from "./Common/HelperFunctions";
import { CREATE_SHIFT_24HOURS_DURATION_ERROR_MSG } from "../../../Shared/AppMessages";

export interface AddNewShiftProps {
  reqistionShiftId: string;
  targetStartDate: any;
  targetEndDate: any;
  dataItem?: any;
  onCloseModal?: any;
  onOpenModal: any;
  parent?: any;
  positionName?: string;
  positionId?: string;
  capacity?: number;
}

export interface AddNewShiftState {
  reqShiftId: string;
  reqShiftIntId: number;
  reqId: string | null;
  name: string;
  positionId: string | null;
  positionName: string;
  capacity: number;
  duration: number;
  startTime: Date;
  endTime: Date;
  startDate: Date | null;
  endDate: Date | null;
  recurrencePattern: number;
  createdDate: string | null;
  createdBy: string | null;
  modifiedDate: string | null;
  modifiedBy: string | null;
  recordStatus: number;
  recurEvery: number;
  weekDays: {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
  };
  submitted: boolean;
  showLoader: boolean;
  positions: Array<{ id: string; name: string }>;
  toggleShiftDetails: boolean;
  toggleRecurrencePattern: boolean;
  showError: boolean;
  shiftTypeId: string | null;
  shiftTypes: Array<{ id: string; name: string }>;
}

class AddNewShift extends React.Component<AddNewShiftProps, AddNewShiftState> {
  public isSaveClicked = false;
  public alertMessage;
  public reqShiftId_tmp?: string;

  constructor(props: AddNewShiftProps) {
    super(props);

    // Initialize with default times similar to PositionDetails.tsx
    const defaultStartTime = new Date(
      new Date(new Date().setHours(9)).setMinutes(0)
    );
    const defaultEndTime = new Date(
      new Date(new Date().setHours(17)).setMinutes(30)
    );

    // Check if we have a dataItem with reqShiftId for editing
    const reqShiftId =
      props.dataItem && props.dataItem.reqShiftId
        ? props.dataItem.reqShiftId
        : props.reqistionShiftId || undefined;

    this.state = {
      reqShiftId: reqShiftId,
      reqShiftIntId: 0,
      reqId: null,
      name: "",
      positionId: props.positionId || null,
      positionName: props.positionName || "",
      capacity: props.capacity || 1,
      duration: 8,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      startDate: null,
      endDate: null,
      recurrencePattern: 2,
      createdDate: null,
      createdBy: null,
      modifiedDate: null,
      modifiedBy: null,
      recordStatus: 1,

      recurEvery: 1,
      weekDays: {
        sunday: false,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
      },

      submitted: false,
      showLoader: false,
      positions: [{ id: props.positionId, name: props.positionName }],
      toggleShiftDetails: true,
      toggleRecurrencePattern: true,
      showError: false,
      shiftTypeId: null,
      shiftTypes: [],
    };
  }

  componentDidMount() {
    this.getReqShifts();
    this.getShiftTypes();

    if (this.props.dataItem) {
      try {
        // Convert string dates and times to Date objects if needed
        const startDate = this.props.dataItem.startDate
          ? this.props.dataItem.startDate instanceof Date
            ? this.props.dataItem.startDate
            : new Date(this.props.dataItem.startDate)
          : null;

        const endDate = this.props.dataItem.endDate
          ? this.props.dataItem.endDate instanceof Date
            ? this.props.dataItem.endDate
            : new Date(this.props.dataItem.endDate)
          : null;

        // Create default times if null
        const defaultStartTime = new Date(
          new Date(new Date().setHours(9)).setMinutes(0)
        );
        const defaultEndTime = new Date(
          new Date(new Date().setHours(17)).setMinutes(30)
        );

        // Use default times if startTime or endTime is null
        const startTime = this.props.dataItem.startTime
          ? parseTimeString(this.props.dataItem.startTime)
          : defaultStartTime;

        const endTime = this.props.dataItem.endTime
          ? parseTimeString(this.props.dataItem.endTime)
          : defaultEndTime;

        // Ensure we have the correct reqShiftId for editing
        // First try to get it from the dataItem, then from props
        const reqShiftId =
          this.props.dataItem.reqShiftId ||
          this.props.reqistionShiftId ||
          undefined;

        // Store the temporary ID in a class property so we can use it when saving
        if (this.props.dataItem.reqShiftId_tmp) {
          this.reqShiftId_tmp = this.props.dataItem.reqShiftId_tmp;
        }

        // Calculate initial duration based on start and end times
        const duration = calculateDuration(startTime, endTime);

        this.setState({
          reqShiftId: reqShiftId,
          name: this.props.dataItem.name,
          reqShiftIntId: this.props.dataItem.reqShiftIntId || 0,
          reqId: this.props.dataItem.reqId || null,
          positionId:
            this.props.dataItem.positionId || this.props.positionId || null,
          positionName:
            this.props.dataItem.positionName || this.props.positionName || "",
          capacity: this.props.dataItem.capacity || this.props.capacity || 1,
          duration: duration, // Set calculated duration
          startTime: startTime,
          endTime: endTime,
          startDate: startDate,
          endDate: endDate,
          recurrencePattern: this.props.dataItem.recurrencePattern || 2,
          recordStatus: this.props.dataItem.recordStatus || 1,

          // Recurrence details if available
          recurEvery: this.props.dataItem.recurEvery || 1,
          weekDays: this.props.dataItem.weekDays || {
            sunday: false,
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: false,
          },

          showLoader: false,
          submitted: false,

          positions: this.state.positions,
          shiftTypeId: this.props.dataItem.shiftTypeId || null,
        });
      } catch (error) {
        errorToastr("Error loading shift data");
      }
    }
  }

  getReqShifts = () => {
    // Only fetch shift data if we have a valid reqistionShiftId (for editing)
    if (this.props.reqistionShiftId) {
      getReqShiftService(this.props.reqistionShiftId).then((res) => {
        return res.data;
      });
    }
  };

  getShiftTypes = () => {
    getShiftTypesService().then((res) => {
      if (res.data) {
        this.setState({ shiftTypes: res.data });
        // Set default shift type if no shiftTypeId is set
        if (!this.state.shiftTypeId) {
          const regularShift = res.data.find( item => item.isDefault === true ) || res.data[0]
          if (regularShift) {
            this.setState({ shiftTypeId: regularShift.id });
          }
        }
      }
    });
  };

  onRecurrencePatternChange = (event) => {
    const newPattern = Number(event.target.value);
    if (newPattern !== 2) {
      // Reset weekdays when switching to Daily or Monthly
      this.setState({
        recurrencePattern: newPattern,
        weekDays: {
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
        },
      });
    } else {
      // When switching to Weekly, set default weekdays (Mon-Fri)
      this.setState({
        recurrencePattern: newPattern,
        weekDays: {
          sunday: false,
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
        },
      });
    }
  };

  handleCheckboxChange(e, modelProp) {
    var stateObj = {};
    stateObj[modelProp] =
      e.target.type == "checkbox" ? e.target.checked : e.target.value;
    this.setState(stateObj);
  }

  openNew = () => {
    this.props.onOpenModal();
  };

  handleSave = () => {
    this.setState({ submitted: true });
    //startDate and EndDate can not be outside of req start and enddate
    if(this.state.startDate < this.props.targetStartDate)
      {
        infoToastr('Start Date Can not be less than Req Start Date');
        return;
      }
      if(this.state.endDate > this.props.dataItem.targetEndDate)
      {
        infoToastr('End Date Can not be greater than Req End Date');
        return;
      }
      if (this.state.duration > 24) {
        errorToastr(CREATE_SHIFT_24HOURS_DURATION_ERROR_MSG);
        return false;
      }  
      
    // Create default times if null
    const defaultStartTime = new Date(
      new Date(new Date().setHours(9)).setMinutes(0)
    );
    const defaultEndTime = new Date(
      new Date(new Date().setHours(17)).setMinutes(30)
    );

    // Ensure startTime and endTime are valid Date objects
    let startTime;
    let endTime;

    try {
      // Try to create proper Date objects
      startTime =
        this.state.startTime instanceof Date
          ? new Date(this.state.startTime)
          : this.state.startTime
          ? new Date(this.state.startTime)
          : defaultStartTime;

      endTime =
        this.state.endTime instanceof Date
          ? new Date(this.state.endTime)
          : this.state.endTime
          ? new Date(this.state.endTime)
          : defaultEndTime;

      // Validate that these are proper Date objects
      startTime.getTime();
      endTime.getTime();
    } catch (error) {
      startTime = defaultStartTime;
      endTime = defaultEndTime;
      errorToastr("Error with time values, using default times");
    }

    // Generate recurrence rule
    const recurrenceRule = generateRecurrenceRule(
      this.state.recurrencePattern,
      this.state.recurEvery,
      this.state.weekDays,
      this.state.endDate
    );

    // Create the data object
    let data: any = {
      name: this.state.name,
      positionId: this.state.positionId,
      positionName: this.state.positionName,
      capacity: this.state.capacity,
      duration: this.state.duration,
      startTime: startTime,
      endTime: endTime,
      startDate:
        this.state.startDate instanceof Date
          ? new Date(this.state.startDate)
          : this.state.startDate
          ? new Date(this.state.startDate)
          : null,
      endDate:
        this.state.endDate instanceof Date
          ? new Date(this.state.endDate)
          : this.state.endDate
          ? new Date(this.state.endDate)
          : null,
      recurrencePattern: this.state.recurrencePattern,
      recordStatus: this.state.recordStatus,
      recurEvery: this.state.recurEvery,
      weekDays: this.state.weekDays,
      recurrenceRule: recurrenceRule,
      shiftTypeId: this.state.shiftTypeId,
    };

    // Additional validation
    if (!(data.startTime instanceof Date)) {
      errorToastr("Start time is not a valid Date object");
      return;
    }

    if (!(data.endTime instanceof Date)) {
      errorToastr("End time is not a valid Date object");
      return;
    }

    let isValid = this.checkValidations();
    if (isValid) {
      // Instead of making an API call, pass the data to the parent component
      const parentComponent = (this.props as any).parent;
      if (parentComponent && typeof parentComponent.addShift === "function") {
        parentComponent.addShift(data);
        this.props.onCloseModal();
      } else {
        errorToastr("Unable to save shift data");
      }
    }
  };

  checkValidations = () => {
    if (!this.state.name || !this.state.endDate) {
      this.setState({ showError: true });
      return false;
    }

    // Basic validations
    if (!this.state.name) {
      errorToastr("Shift name is required.");
      return false;
    }
    if (!this.state.endDate) {
      errorToastr("End Date is required.");
      return false;
    }

    // Get current shift's times and dates
    const currentStartTime = new Date(this.state.startTime);
    const currentEndTime = new Date(this.state.endTime);
    const currentStartDate = new Date(this.state.startDate);
    const currentEndDate = new Date(this.state.endDate);

    // Check if end time is after start time
    // if (currentEndTime <= currentStartTime) {
    //   errorToastr("End Time must be after Start Time.");
    //   return false;
    // }

    // Check if end date is after start date
    if (currentEndDate <= currentStartDate) {
      errorToastr("End Date must be after Start Date.");
      return false;
    }

    // Get existing shifts from parent component
    const parentComponent = (this.props as any).parent;
    if (parentComponent?.state?.shifts) {
      // Filter out the shift being edited and deleted/hidden shifts
      const existingShifts = parentComponent.state.shifts.filter((shift) => {
        // Skip deleted or hidden shifts
        if (shift.isDeleted || shift.hidden) {
          return false;
        }

        // Skip the shift being edited by checking all possible IDs
        if (this.props.dataItem) {
          // Check reqShiftId
          if (
            shift.reqShiftId &&
            shift.reqShiftId === this.props.dataItem.reqShiftId
          ) {
            return false;
          }
          // Check reqShiftId_tmp
          if (
            shift.reqShiftId_tmp &&
            shift.reqShiftId_tmp === this.props.dataItem.reqShiftId_tmp
          ) {
            return false;
          }
          // Check temporary ID
          if (
            this.reqShiftId_tmp &&
            shift.reqShiftId_tmp === this.reqShiftId_tmp
          ) {
            return false;
          }
        }
        return true;
      });

      // function to check if two time ranges overlap
      const doTimesOverlap = (
        start1: Date,
        end1: Date,
        start2: Date,
        end2: Date
      ) => {
        return start1 < end2 && end1 > start2;
      };

     
      // Check each shift for overlap
      for (const shift of existingShifts) {
        // First check if shift names match
        if (shift.name.toLowerCase() === this.state.name.toLowerCase() && shift.shiftTypeId===this.state.shiftTypeId) {
          const shiftStartDate = new Date(shift.startDate);
          const shiftEndDate = new Date(shift.endDate);

          // Check if date ranges overlap
          const datesOverlap = !(
            currentEndDate < shiftStartDate || currentStartDate > shiftEndDate
          );

          if (datesOverlap) {
            // For each day in the overlapping range, check time overlap
            const startDate = new Date(
              Math.max(currentStartDate.getTime(), shiftStartDate.getTime())
            );

            // Get shift times
            const shiftStartTime = new Date(shift.startTime);
            const shiftEndTime = new Date(shift.endTime);

            // Set the times to the same date for proper comparison
            const currentStart = new Date(startDate);
            currentStart.setHours(
              currentStartTime.getHours(),
              currentStartTime.getMinutes()
            );
            const currentEnd = new Date(startDate);
            currentEnd.setHours(
              currentEndTime.getHours(),
              currentEndTime.getMinutes()
            );
            const shiftStart = new Date(startDate);
            shiftStart.setHours(
              shiftStartTime.getHours(),
              shiftStartTime.getMinutes()
            );
            const shiftEnd = new Date(startDate);
            shiftEnd.setHours(
              shiftEndTime.getHours(),
              shiftEndTime.getMinutes()
            );

            // Check for time overlap
            if (
              doTimesOverlap(currentStart, currentEnd, shiftStart, shiftEnd)
            ) {
              warningToastr(
                `Shift "${shift.name}" overlaps with the selected shift time.`
              );
              return false;
            }
          }
        }
      }
    }

    return true;
  };

  handleFilterChange(event) {
    var name = event.target.props.id;
    var originalArray = "original" + name;
    this.state[name] = this.filterData(event.filter, originalArray);
    this.setState(this.state);
  }

  filterData(filter, originalArray) {
    const data1 = this.state[originalArray];
    return filterBy(data1, filter);
  }

  // Event handlers
  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    this.setState((prevState) => ({
      ...prevState,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  handleTimeChange = (e: any, field: "startTime" | "endTime") => {
    if (e.value) {
      const newTime = new Date(e.value);

      if (field === "startTime") {
        this.setState({ startTime: newTime }, () => {
          // Update duration after state is set
          let totalDuration = 0;
          let timeOut = RemoveSecFromTime(this.state.endTime);
          let timeIn = RemoveSecFromTime(this.state.startTime);
          if ((this.state.endTime != null || this.state.endTime != undefined) && this.state.endTime < this.state.startTime) {
              timeOut = new Date(this.state.endTime).setDate(new Date(this.state.endTime).getDate() + 1);
              timeOut = RemoveSecFromTime(timeOut);
          }
          totalDuration = (timeOut - timeIn) / 36e5;

          // const duration = calculateDuration(newTime, this.state.endTime);
          this.setState({ duration: totalDuration });
          
        });
      } else if (field === "endTime") {
        this.setState({ endTime: newTime }, () => {
          // Update duration after state is set
          // const duration = calculateDuration(
          //   this.state.startTime,
          //   newTime
          // );

          let totalDuration = 0;
          let timeOut = RemoveSecFromTime(this.state.endTime);
          let timeIn = RemoveSecFromTime(this.state.startTime);
          if ((this.state.endTime != null || this.state.endTime != undefined) && this.state.endTime < this.state.startTime) {
              timeOut = new Date(this.state.endTime).setDate(new Date(this.state.endTime).getDate() + 1);
              timeOut = RemoveSecFromTime(timeOut);
          }
          totalDuration = (timeOut - timeIn) / 36e5;
          this.setState({ duration: totalDuration });
        });
      }
    }
  };

  handleDateChange = (e: any, field: "startDate" | "endDate") => {
    this.setState(
      (prevState: AddNewShiftState) =>
        ({
          ...prevState,
          [field]: e.value,
        } as AddNewShiftState)
    );
  };

  handleWeekDayChange = (day: keyof typeof this.state.weekDays) => {
    this.setState((prevState) => ({
      weekDays: {
        ...prevState.weekDays,
        [day]: !prevState.weekDays[day],
      },
    }));
  };
 
  render() {
    return (
      <div className="row mt-0 ml-0 mr-0 mt-lg-0 align-items-center mb-0 d-flex justify-content-end holdposition-width">
        <div className="modal-content border-0">
          <div className="modal-header rounded-0 bg-blue d-flex justify-content-center align-items-center pt-2 pb-2">
            <h4 className="modal-title text-white fontFifteen">
              {this.props.dataItem.reqId !== undefined ? "Edit " : "Create "} Shift
            </h4>
            <button
              type="button"
              className="close text-white close_opacity"
              data-dismiss="modal"
              onClick={this.props.onCloseModal}
            >
              &times;
            </button>
          </div>

          {this.state.showLoader &&
            Array.from({ length: 3 }).map((item, i) => (
              <div className="row col-12 mx-auto mt-2" key={i}>
                {Array.from({ length: 3 }).map((item, j) => (
                  <div className="col-12 col-sm-6 col-lg-4 mt-sm-0 mt-1" key={j}>
                    <Skeleton width={100} />
                    <Skeleton height={30} />
                  </div>
                ))}
              </div>
            ))}

          {!this.state.showLoader && (
            <div className="col-12 px-4 newShift-inner-content mt-3">
              <Collapsible
                trigger="Shift Details"
                open={this.state.toggleShiftDetails}
                onTriggerOpening={() => this.setState({ toggleShiftDetails: true })}
                onTriggerClosing={() => this.setState({ toggleShiftDetails: false })}
              >
                <div className="row">
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold required">Shift Name:</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="Enter Shift Name"
                        maxLength={100}
                        value={this.state.name}
                        onChange={this.handleInputChange}
                      />
                      {this.state.showError && (this.state.name == "" || !this.state.name) && (
                        <ErrorComponent />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold required">Shift Type:</label>
                      <DropDownList
                        className="form-control"
                        data={this.state.shiftTypes}
                        textField="name"
                        dataItemKey="id"
                        name="shiftTypeId"
                        value={this.state.shiftTypes.find((type) => type.id === this.state.shiftTypeId) || null}
                        onChange={(e) => {
                          if (e.target.value) {
                            this.setState({
                              shiftTypeId: e.target.value.id,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">Position:</label>
                      <DropDownList
                        className="form-control"
                        data={this.state.positions}
                        textField="name"
                        dataItemKey="id"
                        name="positionId"
                        value={this.state.positions.find((p) => p.id === this.state.positionId) || null}
                        onChange={(e) => {
                          if (e.target.value) {
                            this.setState({
                              positionId: e.target.value.id,
                              positionName: e.target.value.name,
                            });
                          }
                        }}
                        disabled={true}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">No of Positions Required:</label>
                      <input
                        type="number"
                        name="capacity"
                        className="form-control text-right"
                        placeholder="Enter No of Positions Required"
                        value={this.state.capacity}
                        onChange={this.handleInputChange}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">Duration (hrs):</label>
                      <input
                        type="number"
                        name="duration"
                        className="form-control"
                        placeholder="Duration in Hours"
                        value={this.state.duration}
                        readOnly={true}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">Start Date:</label>
                      <DatePicker
                        className="form-control"
                        format="MM/dd/yyyy"
                        name="startDate"
                        value={
                          this.state.startDate != undefined &&
                          this.state.startDate != null
                            ? new Date(this.state.startDate)
                            : null
                        }
                        onChange={(e) => this.handleDateChange(e, "startDate")}                        
                        min={new Date(new Date(this.props.targetStartDate).setDate(new Date(this.props.targetStartDate).getDate()))}
                        max={
                          this.state.endDate
                            ? new Date(
                                new Date(this.state.endDate).setDate(
                                  new Date(this.state.endDate).getDate() - 1
                                )
                              )
                            : new Date(
                                new Date().setFullYear(
                                  new Date().getFullYear() + 1
                                )
                              )
                        }
                        formatPlaceholder="formatPattern"
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">End Date:</label>
                      <DatePicker
                        className="form-control"
                        format="MM/dd/yyyy"
                        name="endDate"
                        value={
                          this.state.endDate != undefined &&
                          this.state.endDate != null
                            ? new Date(this.state.endDate)
                            : null
                        }
                        min={
                          new Date(
                            new Date(this.state.startDate).setDate(
                              new Date(this.state.startDate).getDate() + 1
                            )
                          )
                        }
                        max={this.state.endDate ? new Date(new Date(this.props.targetEndDate).setDate(new Date(this.props.targetEndDate).getDate())) : new Date(new Date().setFullYear(new Date().getFullYear() + 1))}                                           
                        onChange={(e) => this.handleDateChange(e, "endDate")}
                        formatPlaceholder="formatPattern"
                      />
                      {this.state.showError && !this.state.endDate && <ErrorComponent />}
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">Start Time:</label>
                      <TimePicker
                        className="form-control"
                        format="hh:mm a"
                        name="startTime"
                        value={new Date(this.state.startTime)}
                        // max={new Date(this.state.endTime)}
                        onChange={(e) => this.handleTimeChange(e, "startTime")}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <div className="form-group">
                      <label className="font-weight-bold">End Time:</label>
                      <TimePicker
                        className="form-control"
                        format="hh:mm a"
                        // min={new Date(this.state.startTime)}
                        name="endTime"
                        value={new Date(this.state.endTime)}
                        onChange={(e) => this.handleTimeChange(e, "endTime")}
                      />
                    </div>
                  </div>
                </div>
              </Collapsible>

              <Collapsible
                trigger="Recurrence Pattern"
                open={this.state.toggleRecurrencePattern}
                onTriggerOpening={() => this.setState({ toggleRecurrencePattern: true })}
                onTriggerClosing={() => this.setState({ toggleRecurrencePattern: false })}
              >
                <div className="row mt-3">
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex flex-column mb-2 mb-sm-0">
                      <label className="container container_checkboxandradio radioBtnCustom font-weight-bold">
                        Daily
                        <input
                          type="radio"
                          name="recurrencePattern"
                          value="1"
                          checked={this.state.recurrencePattern === 1}
                          onChange={this.onRecurrencePatternChange}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <label className="container container_checkboxandradio radioBtnCustom font-weight-normal">
                        Weekly
                        <input
                          type="radio"
                          name="recurrencePattern"
                          value="2"
                          checked={this.state.recurrencePattern === 2}
                          onChange={this.onRecurrencePatternChange}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <label className="container container_checkboxandradio radioBtnCustom font-weight-normal">
                        Monthly
                        <input
                          type="radio"
                          name="recurrencePattern"
                          value="3"
                          checked={this.state.recurrencePattern === 3}
                          onChange={this.onRecurrencePatternChange}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                  </div>
                  {this.state.recurrencePattern === 2 && (
                    <div className="col-12 col-sm-6 col-lg-9">
                      <div className="row">
                        <div className="col-12 d-flex">
                          <label className="mb-1 font-weight-bold">
                            Recur Every
                          </label>
                          <input
                            type="number"
                            name="recurEvery"
                            className="form-control mx-2"
                            style={{ width: "60px" }}
                            value={this.state.recurEvery}
                            onChange={this.handleInputChange}
                            min="1" max="10"
                          />
                          <label className="mb-1 font-weight-bold">
                            Weeks On:
                          </label>
                        </div>
                      </div>
                      <div className="row">
                        {Object.entries(this.state.weekDays).map(
                          ([day, checked]) => (
                            <div
                              key={day}
                              className="col-12 col-sm-6 col-lg-3 mt-3"
                            >
                              <label className="container-R d-flex mb-0 pb-2 font-weight-bold">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    this.handleWeekDayChange(
                                      day as keyof typeof this.state.weekDays
                                    )
                                  }
                                />
                                <span
                                  className="checkmark-R checkPosition checkPositionTop"
                                  style={{ left: "0px" }}
                                ></span>
                                {day.charAt(0).toUpperCase() + day.slice(1)}
                              </label>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

{this.state.recurrencePattern == 3 &&(
                                        <div className="col-12 col-sm-6 col-lg-9 mt-2">
                                            <div className="row mb-2">
                                            <div className="col-12 d-flex">
                                                <label className="font-weight-bold">Recur every</label>
                                                <input
                                                    type="number"
                                                    name="recurEvery"
                                                    className="form-control mx-2 recurEveryInput"
                                                    style={{ width: '60px' }}
                                                    min={1}
                                                    max={31}
                                                    value={this.state.recurEvery}
                                                    onChange={this.handleInputChange}
                                                />
                                                <label className="font-weight-bold"> day of the month</label>
                                            </div>
                                        </div>
                                    </div>)}
                </div>
              </Collapsible>
            </div>
          )}
          <div className="modal-footer justify-content-center border-0 mt-3 mb-3">
            <div className="row mt-2 mb-2 ml-0 mr-0 justify-content-center">
              <button
                type="button"
                className="btn button button-close mr-2 shadow mb-2 mb-xl-0"
                onClick={this.props.onCloseModal}
              >
                <FontAwesomeIcon icon={faTimesCircle} className={"mr-1"} /> Close
              </button>
              <button
                type="button"
                className="btn button button-bg mr-2 shadow mb-2 mb-xl-0"
                onClick={this.handleSave}
              >
                <FontAwesomeIcon icon={faSave} className={"mr-1"} /> Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
export default AddNewShift;
